<template>
  <div id="bulkUploadModal" class="o-modal" style="z-index:99;">
    <div class="o-modal-content" style="max-width: 800px;">
      <div class="o-modal-header">
        <a @click="closeModal()" class="close clickable">
          <i class="far fa-times"></i>
        </a>
        <p class="lead"><i class="fas fa-layer-group"></i> Bulk Upload Datasets</p>
        <small class="text-muted">Upload up to 10 datasets at once</small>
      </div>
      
      <div class="o-modal-body">
        <!-- File Selection -->
        <div v-if="selectedFiles.length === 0" class="file-selection-area text-center p-4 mb-3" style="border: 2px dashed #dee2e6; border-radius: 8px;">
          <input
            type="file"
            ref="fileInput"
            class="d-none"
            accept=".csv,.txt,.zip,.geojson,.tif,.tiff,.kmz,.kml"
            multiple
            @change="onFilesSelected"
            id="bulkFileInput"
          />
          <label for="bulkFileInput" class="clickable">
            <i class="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
            <p class="mb-0"><strong>Click to select files</strong></p>
            <small class="text-muted">or drag and drop files here</small>
          </label>
        </div>

        <!-- File List -->
        <div v-if="selectedFiles.length > 0" class="file-list">
          <div class="mb-3">
            <button
              @click="$refs.fileInput.click()"
              class="btn btn-sm btn-outline-primary"
              :disabled="selectedFiles.length >= 10 || isUploading"
            >
              <i class="fas fa-plus"></i> Add More Files
            </button>
            <span class="ml-2 text-muted">
              {{ selectedFiles.length }} / 10 files selected
            </span>
          </div>

          <div
            v-for="(fileItem, index) in selectedFiles"
            :key="index"
            class="file-item mb-3 p-3"
            :class="getFileItemClass(fileItem)"
            style="border: 1px solid #dee2e6; border-radius: 6px;"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <div class="d-flex align-items-center mb-2">
                  <i :class="getFileIcon(fileItem.file.name)"></i>
                  <strong class="ml-2">{{ fileItem.file.name }}</strong>
                  <small class="ml-2 text-muted">({{ formatFileSize(fileItem.file.size) }})</small>
                  <span v-if="fileItem.status === 'success'" class="badge badge-success ml-2">
                    <i class="fas fa-check"></i> Success
                  </span>
                  <span v-else-if="fileItem.status === 'error'" class="badge badge-danger ml-2">
                    <i class="fas fa-times"></i> Error
                  </span>
                  <span v-else-if="fileItem.status === 'processing'" class="badge badge-info ml-2">
                    <i class="fas fa-spinner fa-spin"></i> Processing
                  </span>
                  <span v-else-if="fileItem.status === 'uploading'" class="badge badge-primary ml-2">
                    <i class="fas fa-upload"></i> Uploading
                  </span>
                </div>

                <!-- Name Input -->
                <div class="form-group mb-2">
                  <label :for="'fileName_' + index" class="small mb-1">Dataset Name</label>
                  <input
                    :id="'fileName_' + index"
                    v-model="fileItem.name"
                    type="text"
                    class="form-control form-control-sm"
                    :placeholder="getDefaultName(fileItem.file.name)"
                    :disabled="isUploading || fileItem.status === 'processing' || fileItem.status === 'uploading'"
                  />
                </div>

                <!-- Description Input -->
                <div class="form-group mb-2">
                  <label :for="'fileDesc_' + index" class="small mb-1">Description (optional)</label>
                  <textarea
                    :id="'fileDesc_' + index"
                    v-model="fileItem.description"
                    class="form-control form-control-sm"
                    rows="2"
                    :disabled="isUploading || fileItem.status === 'processing' || fileItem.status === 'uploading'"
                  ></textarea>
                </div>

                <!-- Progress Bar -->
                <div v-if="fileItem.status === 'uploading' || fileItem.status === 'processing'" class="mt-2">
                  <div class="progress" style="height: 4px;">
                    <div
                      class="progress-bar"
                      :class="{
                        'progress-bar-striped': fileItem.status === 'processing',
                        'progress-bar-animated': fileItem.status === 'processing'
                      }"
                      role="progressbar"
                      :style="{ width: fileItem.progress + '%' }"
                    ></div>
                  </div>
                  <small class="text-muted">{{ fileItem.progress }}%</small>
                </div>

                <!-- Error Message -->
                <div v-if="fileItem.status === 'error'" class="alert alert-danger mt-2 mb-0 py-2">
                  <small>{{ fileItem.error }}</small>
                </div>
              </div>

              <!-- Remove Button -->
              <button
                v-if="fileItem.status !== 'uploading' && fileItem.status !== 'processing'"
                @click="removeFile(index)"
                class="btn btn-sm btn-outline-danger ml-2"
                :disabled="isUploading"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Upload Summary -->
        <div v-if="uploadResults.total > 0" class="upload-summary mt-4 p-3" :class="uploadResults.failed > 0 ? 'alert-warning' : 'alert-success'" style="border-radius: 6px;">
          <h6><strong>Upload Complete</strong></h6>
          <p class="mb-0">
            Successfully uploaded <strong>{{ uploadResults.success }}</strong> of <strong>{{ uploadResults.total }}</strong> datasets.
            <span v-if="uploadResults.failed > 0">
              <strong>{{ uploadResults.failed }}</strong> failed.
            </span>
          </p>
        </div>
      </div>

      <div class="o-modal-footer">
        <button
          @click="closeModal()"
          class="btn btn-secondary"
          :disabled="isUploading"
        >
          {{ uploadResults.total > 0 ? 'Close' : 'Cancel' }}
        </button>
        <button
          v-if="selectedFiles.length > 0 && uploadResults.total === 0"
          @click="uploadAllFiles()"
          class="btn btn-primary"
          :disabled="isUploading || !canUpload"
        >
          <i class="fas fa-upload"></i> Upload All ({{ selectedFiles.length }})
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import firebase from 'firebase/compat/app'
import geoblaze from 'geoblaze'
import proj4 from 'proj4'
import { deriveHeaders } from '@/lib/deriveHeaders'
const epsg = require('epsg')
const fb = require('../../firebaseConfig.js')

export default {
  name: 'BulkUploadModal',
  data() {
    return {
      selectedFiles: [], // Array of { file, name, description, status, progress, error, dataFileObject }
      isUploading: false,
      uploadResults: {
        total: 0,
        success: 0,
        failed: 0
      }
    }
  },
  computed: {
    ...mapState(['farm', 'currentUser', 'folder']),
    canUpload() {
      return this.selectedFiles.length > 0 && 
             this.selectedFiles.every(f => f.status !== 'uploading' && f.status !== 'processing') &&
             !this.isUploading
    }
  },
  methods: {
    closeModal() {
      if (!this.isUploading) {
        this.$emit('close')
        // Reset state
        this.selectedFiles = []
        this.uploadResults = { total: 0, success: 0, failed: 0 }
        this.isUploading = false
      }
    },
    
    onFilesSelected(event) {
      const files = Array.from(event.target.files)
      const remainingSlots = 10 - this.selectedFiles.length
      
      if (files.length > remainingSlots) {
        this.$store.commit('setAlert', {
          active: true,
          message: `You can only upload ${remainingSlots} more file(s). Only the first ${remainingSlots} will be added.`,
          level: 'alert-warning',
          timeout: 5000
        })
        files.splice(remainingSlots)
      }

      // Validate and add files
      files.forEach(file => {
        if (this.validateFile(file)) {
          const defaultName = this.getDefaultName(file.name)
          this.selectedFiles.push({
            file: file,
            name: defaultName,
            description: '',
            status: 'pending',
            progress: 0,
            error: null,
            dataFileObject: null
          })
        }
      })

      // Reset input
      event.target.value = ''
    },

    validateFile(file) {
      // Check file type
      const validExtensions = ['csv', 'txt', 'zip', 'geojson', 'tif', 'tiff', 'kmz', 'kml']
      const fileExt = file.name.split('.').pop().toLowerCase()
      
      if (!validExtensions.includes(fileExt)) {
        this.$store.commit('setAlert', {
          active: true,
          message: `File "${file.name}" has an invalid extension. Supported: ${validExtensions.join(', ')}`,
          level: 'alert-danger',
          timeout: 5000
        })
        return false
      }

      // Check file size (warn if > 100MB)
      const maxSize = 100 * 1024 * 1024 // 100MB
      if (file.size > maxSize) {
        this.$store.commit('setAlert', {
          active: true,
          message: `File "${file.name}" is very large (${this.formatFileSize(file.size)}). Upload may take a while.`,
          level: 'alert-warning',
          timeout: 5000
        })
      }

      // Check for duplicates
      const duplicate = this.selectedFiles.find(f => f.file.name === file.name)
      if (duplicate) {
        this.$store.commit('setAlert', {
          active: true,
          message: `File "${file.name}" is already selected.`,
          level: 'alert-warning',
          timeout: 3000
        })
        return false
      }

      return true
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1)
    },

    getDefaultName(fileName) {
      // Remove extension and return as default name
      return fileName.replace(/\.[^/.]+$/, '')
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase()
      const icons = {
        csv: 'fas fa-file-csv text-success',
        txt: 'fas fa-file-alt text-info',
        zip: 'fas fa-file-archive text-warning',
        geojson: 'fas fa-map text-primary',
        tif: 'fas fa-image text-danger',
        tiff: 'fas fa-image text-danger',
        kmz: 'fas fa-map-marked-alt text-primary',
        kml: 'fas fa-map-marked-alt text-primary'
      }
      return icons[ext] || 'fas fa-file text-secondary'
    },

    getFileItemClass(fileItem) {
      return {
        'border-success': fileItem.status === 'success',
        'border-danger': fileItem.status === 'error',
        'border-primary': fileItem.status === 'uploading' || fileItem.status === 'processing'
      }
    },

    async uploadAllFiles() {
      if (this.isUploading || this.selectedFiles.length === 0) return

      this.isUploading = true
      this.uploadResults = { total: this.selectedFiles.length, success: 0, failed: 0 }

      // Process all files in parallel
      const uploadPromises = this.selectedFiles.map((fileItem, index) => 
        this.processFile(fileItem, index)
      )

      // Wait for all uploads to complete
      await Promise.allSettled(uploadPromises)

      this.isUploading = false

      // Show summary
      const successCount = this.selectedFiles.filter(f => f.status === 'success').length
      const failedCount = this.selectedFiles.filter(f => f.status === 'error').length

      if (successCount > 0) {
        this.$store.commit('setAlert', {
          active: true,
          message: `Successfully uploaded ${successCount} of ${this.selectedFiles.length} datasets.`,
          level: 'alert-success',
          timeout: 5000
        })
        // Emit success event to refresh datasets list
        this.$emit('success', { success: successCount, total: this.selectedFiles.length })
      }

      if (failedCount > 0) {
        this.$store.commit('setAlert', {
          active: true,
          message: `${failedCount} dataset(s) failed to upload. Check the error messages above.`,
          level: 'alert-warning',
          timeout: 5000
        })
      }
    },

    getFormattedTime() {
      const today = new Date()
      const y = today.getFullYear()
      const m = today.getMonth() + 1
      const d = today.getDate()
      return y + '-' + m + '-' + d + '-'
    },
    
    s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1)
    },

    async processFile(fileItem, index) {
      try {
        fileItem.status = 'uploading'
        fileItem.progress = 0
        fileItem.error = null

        // Generate unique file ID
        const dataFilesId = this.getFormattedTime() + this.s4()

        const file = fileItem.file
        const lastDot = file.name.lastIndexOf('.')
        const fileExt = file.name.substring(lastDot + 1).toLowerCase()

        // Construct storage path
        const dataFilesPath = 'datasets/' + this.farm.id + '/' + dataFilesId + '-' + file.name
        const storageRef = firebase.storage().ref(dataFilesPath)

        // Prepare data file object
        const dataFileObject = {}

        // Handle TIF/TIFF files (raster)
        if (fileExt === 'tif' || fileExt === 'tiff') {
          try {
            const raster = await geoblaze.load(file)
            const firstProjection = epsg['EPSG:' + raster.projection]
            const secondProjection = epsg['EPSG:4326']
            const rasterMins = proj4(firstProjection, secondProjection, [raster.xmin, raster.ymin])
            const rasterMaxs = proj4(firstProjection, secondProjection, [raster.xmax, raster.ymax])
            dataFileObject.xMin = rasterMins[0]
            dataFileObject.xMax = rasterMaxs[0]
            dataFileObject.yMin = rasterMins[1]
            dataFileObject.yMax = rasterMaxs[1]
          } catch (rasterError) {
            console.error('Error processing raster:', rasterError)
            // Continue anyway, cloud function will handle it
          }
        }

        // Upload file to storage. Tag customMetadata.userId so the
        // usageFinalize backend trigger can attribute bytes correctly.
        const uploadTask = storageRef.put(file, {
          customMetadata: {
            userId: (this.$store.state.currentUser && this.$store.state.currentUser.uid) || ''
          }
        })
        
        // Track upload progress
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            fileItem.progress = Math.round(progress)
          },
          (error) => {
            console.error('Upload error:', error)
            fileItem.status = 'error'
            fileItem.error = `Upload failed: ${error.message}`
            this.uploadResults.failed++
            throw error
          },
          async () => {
            // Upload complete
            const dataFilePath = 'datasets/' + this.farm.id + '/' + dataFilesId + '-' + file.name
            dataFileObject.path = dataFilePath
            dataFileObject.originalFileName = file.name

            // Set status to processing
            fileItem.status = 'processing'
            fileItem.progress = 50

            // Wait for cloud function to process the file
            if (fileExt === 'geojson') {
              dataFileObject.geoJsonPath = dataFilePath
              fileItem.dataFileObject = dataFileObject
              await this.createDatasetDocument(fileItem, index)
            } else if (fileExt === 'tif' || fileExt === 'tiff') {
              dataFileObject.raster = true
              fileItem.dataFileObject = dataFileObject
              await this.waitForJpgFile(dataFileObject, fileItem, index)
            } else {
              fileItem.dataFileObject = dataFileObject
              await this.waitForGeoJsonFile(dataFileObject, fileItem, index)
            }
          }
        )
      } catch (error) {
        console.error('Error processing file:', error)
        fileItem.status = 'error'
        fileItem.error = error.message || 'Unknown error occurred'
        this.uploadResults.failed++
      }
    },

    async waitForGeoJsonFile(dataFileObject, fileItem, index) {
      const maxAttempts = 20 // 20 attempts * 3 seconds = 60 seconds max
      let attempts = 0
      const geoJsonDataFilePath = dataFileObject.path.replace(/\.[^/.]+$/, '') + '.geojson'
      const geoJsonStorageRef = firebase.storage().ref(geoJsonDataFilePath)

      while (attempts < maxAttempts) {
        try {
          await new Promise(resolve => setTimeout(resolve, 3000)) // Wait 3 seconds
          const url = await geoJsonStorageRef.getDownloadURL()
          if (url) {
            dataFileObject.geoJsonPath = geoJsonDataFilePath
            fileItem.dataFileObject = dataFileObject
            await this.createDatasetDocument(fileItem, index)
            return
          }
        } catch (error) {
          attempts++
          if (attempts >= maxAttempts) {
            fileItem.status = 'error'
            fileItem.error = 'GeoJSON file was not generated after processing. Please try uploading again.'
            this.uploadResults.failed++
            return
          }
        }
      }
    },

    async waitForJpgFile(dataFileObject, fileItem, index) {
      const maxAttempts = 20
      let attempts = 0
      const jpgFilePath = dataFileObject.path.replace(/\.[^/.]+$/, '') + '.jpg'
      const jpgStorageRef = firebase.storage().ref(jpgFilePath)

      while (attempts < maxAttempts) {
        try {
          await new Promise(resolve => setTimeout(resolve, 3000))
          const url = await jpgStorageRef.getDownloadURL()
          if (url) {
            dataFileObject.jpgPath = jpgFilePath
            fileItem.dataFileObject = dataFileObject
            await this.createDatasetDocument(fileItem, index)
            return
          }
        } catch (error) {
          attempts++
          if (attempts >= maxAttempts) {
            fileItem.status = 'error'
            fileItem.error = 'JPG preview file was not generated after processing. Please try uploading again.'
            this.uploadResults.failed++
            return
          }
        }
      }
    },

    async createDatasetDocument(fileItem, index) {
      try {
        // Get headers from GeoJSON if available
        let headers = []
        if (fileItem.dataFileObject.geoJsonPath) {
          try {
            const geoJsonRef = firebase.storage().ref(fileItem.dataFileObject.geoJsonPath)
            const url = await geoJsonRef.getDownloadURL()
            const response = await fetch(url)
            const geoJson = await response.json()
            if (geoJson.features && geoJson.features.length > 0) {
              headers = deriveHeaders(geoJson)
            }
          } catch (error) {
            console.log('Could not fetch headers from GeoJSON:', error)
          }
        }

        // Create dataset document
        const datasetData = {
          name: fileItem.name || fileItem.dataFileObject.originalFileName,
          description: fileItem.description || '',
          headers: headers,
          farmId: this.farm.id,
          createdOn: new Date(),
          createdBy: this.currentUser.uid,
          updatedOn: new Date(),
          updatedBy: this.currentUser.uid,
          file: fileItem.dataFileObject,
          folderId: this.folder.id || 'root',
          dynamic: false
        }

        await fb.datasetsCollection.add(datasetData)
        
        fileItem.status = 'success'
        fileItem.progress = 100
        this.uploadResults.success++
      } catch (error) {
        console.error('Error creating dataset document:', error)
        fileItem.status = 'error'
        fileItem.error = `Failed to create dataset: ${error.message}`
        this.uploadResults.failed++
      }
    }
  }
}
</script>

<style scoped>
.o-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.o-modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 800px;
}

.o-modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.o-modal-header .close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.o-modal-header .close:hover {
  color: #333;
}

.o-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.o-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  height: 75px;
}

.file-selection-area {
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-selection-area:hover {
  border-color: #007bff !important;
  background-color: #f8f9fa;
}

.file-item {
  transition: all 0.2s ease;
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  opacity: 0.8;
}
</style>
